// Crypto-wallet module parser — converts admin-service wire DTOs to the
// camelCase view-model types consumed everywhere else. Only parsed data is
// allowed past this file.

import type {
  CryptoWalletDto,
  CryptoWalletsResponseDto,
  VaspWalletDetailDto,
  VaspWalletListResponseDto,
  VaspWalletRowDto,
  VaspWalletStatusCountsDto,
  TrustlineDto,
  WalletManagementDetailDto,
  WalletManagementListResponseDto,
  WalletManagementPendingOptInDto,
  WalletManagementRowDto,
  WalletManagementTrustlineDto,
  CryptoWalletAccountDto,
} from './cryptoWalletContracts'
import type {
  CryptoWallet,
  CryptoWalletsResponse,
  VaspWalletDetails,
  VaspWalletListItem,
  VaspWalletListResponse,
  VaspWalletStatusCounts,
  Trustline,
  WalletManagementDetail,
  WalletManagementListResponse,
  WalletManagementPendingOptIn,
  WalletManagementRow,
  WalletManagementTrustline,
  CryptoWalletAccount,
} from '../types/cryptoWalletTypes'
import { CRYPTO_WALLET_STATUS, type CryptoWalletStatus } from '../constants/cryptoWalletStatus'
import { TRUSTLINE_STATUS, type TrustlineStatus } from '../constants/trustlineStatus'

/**
 * The one nullable field in this module that is NOT flattened.
 *
 * `peerAuthorized` is three-state and every state is load-bearing: null means
 * the ledger has never been read, false means it was read and the issuer has
 * not authorised. `Boolean(dto.peerAuthorized)` or `?? false` would report an
 * unread ledger as a confirmed refusal — the exact error the whole axis exists
 * to avoid. `undefined` (a field the server omitted) is as unknown as null and
 * normalises to it.
 *
 * See @shared/constants/trustlineAuthorization for the rendering grammar.
 */
const toPeerAuthorized = (value: boolean | null | undefined): boolean | null => value ?? null

export const parseCryptoWallet = (dto: CryptoWalletDto): CryptoWallet => {
  const investorRaw = dto.investor ?? null
  return {
    id: String(dto.id ?? ''),
    investorId: String(dto.investorId ?? ''),
    asset: String(dto.asset ?? ''),
    network: String(dto.network ?? ''),
    walletAddress: String(dto.walletAddress ?? ''),
    trustlineStatus: String(dto.trustlineStatus ?? ''),
    balance: String(dto.balance ?? '0'),
    status: String(dto.status ?? ''),
    createdAt: String(dto.createdAt ?? ''),
    updatedAt: String(dto.updatedAt ?? ''),
    investor: investorRaw
      ? {
          id: String(investorRaw.id ?? ''),
          firstName: String(investorRaw.firstName ?? ''),
          lastName: String(investorRaw.lastName ?? ''),
          email: investorRaw.email ?? null,
        }
      : null,
    txHash: dto.txHash ?? '',
    // NOT `?? false`. See the note above `toPeerAuthorized`.
    peerAuthorized: toPeerAuthorized(dto.peerAuthorized),
  }
}

export const parseCryptoWalletsResponse = (
  dto: CryptoWalletsResponseDto,
): CryptoWalletsResponse => ({
  rows: (dto.rows ?? []).map(parseCryptoWallet),
  rowsCount: Number(dto.rowsCount ?? dto.total ?? 0),
})

// ── VAISP onboarding wallets ─────────────────────────────────────────────────

// The wire value is the `crypto_wallet_status` DB enum, so it always matches.
// Narrowed through the constant rather than cast so an unrecognised value lands
// on a known label instead of typing as a status the UI has no colour for; the
// badge would render blank and the filter cards would never match it.
const toWalletStatus = (status: string | null | undefined): CryptoWalletStatus => {
  const known = Object.values(CRYPTO_WALLET_STATUS).find((value) => value === status)
  return known ?? CRYPTO_WALLET_STATUS.PENDING_VALIDATION
}

export const parseVaspWalletRow = (dto: VaspWalletRowDto): VaspWalletListItem => ({
  id: String(dto.id ?? ''),
  investorId: String(dto.investorId ?? ''),
  firstName: dto.firstName ?? '',
  lastName: dto.lastName ?? '',
  email: dto.email ?? '',
  walletAddress: dto.walletAddress ?? '',
  blockchainNetwork: dto.blockchainNetwork ?? '',
  walletStatus: toWalletStatus(dto.walletStatus),
  validationMethod: dto.validationMethod ?? '',
  vaspReferenceId: dto.vaspReferenceId ?? '',
  isPrimary: Boolean(dto.isPrimary),
  createdAt: dto.createdAt ?? '',
})

export const parseVaspWalletDetails = (dto: VaspWalletDetailDto): VaspWalletDetails => ({
  ...parseVaspWalletRow(dto),
  validationReference: dto.validationReference ?? '',
  validatedAt: dto.validatedAt ?? '',
  updatedAt: dto.updatedAt ?? '',
  vaspUserId: dto.vaspUserId ?? '',
  vaspUserStatus: dto.vaspUserStatus ?? '',
  vaspProvider: dto.vaspProvider ?? '',
  participantNumber: dto.vaspParticipantNumber ?? '',
  lastError: dto.lastError ?? '',
  lastErrorSource: dto.lastErrorSource ?? '',
  retryCount: Number(dto.retryCount ?? 0),
  lastSyncedAt: dto.lastSyncedAt ?? '',
})

export const parseVaspWalletListResponse = (
  dto: VaspWalletListResponseDto,
): VaspWalletListResponse => ({
  rows: (dto.data ?? []).map(parseVaspWalletRow),
  rowsCount: Number(dto.meta?.total ?? 0),
})

export const parseVaspWalletStatusCounts = (
  dto: VaspWalletStatusCountsDto,
): VaspWalletStatusCounts => ({
  all: Number(dto.all ?? 0),
  pendingValidation: Number(dto.pendingValidation ?? 0),
  active: Number(dto.active ?? 0),
  inactive: Number(dto.inactive ?? 0),
})

// ── Wallet management ────────────────────────────────────────────────────────
// Nullable-to-empty-string is the module's existing convention (see
// parseCryptoWallet above): the templates render '—' on falsy, so a null and an
// empty string must not behave differently. `trustlineStatus` is the exception —
// null there MEANS "no trustline exists", which is the state the screen acts on,
// so it is preserved rather than flattened.

const toTrustlineStatus = (status: string | null | undefined): TrustlineStatus =>
  (Object.values(TRUSTLINE_STATUS) as string[]).includes(status ?? '')
    ? (status as TrustlineStatus)
    : TRUSTLINE_STATUS.PENDING

const parseWalletManagementRow = (dto: WalletManagementRowDto): WalletManagementRow => ({
  investorId: String(dto.investorId ?? ''),
  investorName: dto.investorName ?? '',
  email: dto.email ?? '',
  walletId: dto.walletId ?? '',
  walletAddress: dto.walletAddress ?? '',
  walletStatus: dto.walletStatus ? toWalletStatus(dto.walletStatus) : null,
  blockchainNetwork: dto.blockchainNetwork ?? '',
  validatedAt: dto.validatedAt ?? '',
  activeTrustlines: Number(dto.activeTrustlines ?? 0),
  pendingTrustlines: Number(dto.pendingTrustlines ?? 0),
  failedTrustlines: Number(dto.failedTrustlines ?? 0),
  awaitingOptInOrders: Number(dto.awaitingOptInOrders ?? 0),
})

export const parseWalletManagementListResponse = (
  dto: WalletManagementListResponseDto,
): WalletManagementListResponse => ({
  rows: (dto.data ?? []).map(parseWalletManagementRow),
  rowsCount: Number(dto.meta?.total ?? 0),
})

const parseWalletManagementTrustline = (
  dto: WalletManagementTrustlineDto,
): WalletManagementTrustline => ({
  id: String(dto.id ?? ''),
  propertyId: String(dto.propertyId ?? ''),
  propertyName: dto.propertyName ?? '',
  offeringId: dto.offeringId ?? '',
  offeringTitle: dto.offeringTitle ?? '',
  tokenSymbol: dto.tokenSymbol ?? '',
  tokenHex: dto.tokenHex ?? '',
  issuerWalletAddress: dto.issuerWalletAddress ?? '',
  trustlineStatus: toTrustlineStatus(dto.trustlineStatus),
  activatedAt: dto.activatedAt ?? '',
  failedAt: dto.failedAt ?? '',
  failureReason: dto.failureReason ?? '',
  createdAt: dto.createdAt ?? '',
  txHash: dto.txHash ?? '',
  // Null-preserved, unlike every string above it — see `toPeerAuthorized`.
  peerAuthorized: toPeerAuthorized(dto.peerAuthorized),
})

const parseWalletManagementPendingOptIn = (
  dto: WalletManagementPendingOptInDto,
): WalletManagementPendingOptIn => ({
  orderId: String(dto.orderId ?? ''),
  orderReference: dto.orderReference ?? '',
  offeringId: String(dto.offeringId ?? ''),
  offeringTitle: dto.offeringTitle ?? '',
  propertyId: String(dto.propertyId ?? ''),
  propertyName: dto.propertyName ?? '',
  tokenSymbol: dto.tokenSymbol ?? '',
  tokenHex: dto.tokenHex ?? '',
  issuerWalletAddress: dto.issuerWalletAddress ?? '',
  // Preserved as null on purpose — see the note above.
  trustlineStatus: dto.trustlineStatus ? toTrustlineStatus(dto.trustlineStatus) : null,
  requiresTrustlineSetup: Boolean(dto.requiresTrustlineSetup),
  orderedAt: dto.orderedAt ?? '',
})

export const parseWalletManagementDetail = (
  dto: WalletManagementDetailDto,
): WalletManagementDetail => ({
  investor: {
    investorId: String(dto.investor?.investorId ?? ''),
    investorName: dto.investor?.investorName ?? '',
    email: dto.investor?.email ?? '',
    phone: dto.investor?.phone ?? '',
    walletId: dto.investor?.walletId ?? '',
    walletAddress: dto.investor?.walletAddress ?? '',
    walletStatus: dto.investor?.walletStatus ? toWalletStatus(dto.investor.walletStatus) : null,
    blockchainNetwork: dto.investor?.blockchainNetwork ?? '',
    validationMethod: dto.investor?.validationMethod ?? '',
    vaspReferenceId: dto.investor?.vaspReferenceId ?? '',
    validationReference: dto.investor?.validationReference ?? '',
    validatedAt: dto.investor?.validatedAt ?? '',
    isPrimary: dto.investor?.isPrimary ?? null,
    walletCreatedAt: dto.investor?.walletCreatedAt ?? '',
    walletUpdatedAt: dto.investor?.walletUpdatedAt ?? '',
    // Null-preserved — see `toPeerAuthorized`.
    peerAuthorized: toPeerAuthorized(dto.investor?.peerAuthorized),
    // Two-state by construction on the server, so a coercion is safe here: an
    // unread ledger is not a wait, and the server already resolves that.
    awaitingIssuerAuthorization: Boolean(dto.investor?.awaitingIssuerAuthorization),
    // Kept as a STRING. The value is integer drops (1 XRP = 1,000,000) and
    // exceeds Number.MAX_SAFE_INTEGER, so it must reach `formatXrpFromDrops`
    // unparsed — a Number() here would silently round the low-order drops away.
    xrpBalanceDrops: dto.investor?.xrpBalanceDrops ?? '',
    // Wire calls it `reserveCheckedAt` (it is stamped by the reserve pre-check);
    // the view model keeps the balance-centric name it is rendered beside.
    xrpBalanceReadAt: dto.investor?.reserveCheckedAt ?? '',
  },
  trustlines: (dto.trustlines ?? []).map(parseWalletManagementTrustline),
  pendingOptIns: (dto.pendingOptIns ?? []).map(parseWalletManagementPendingOptIn),
})

export const parseTrustline = (dto: TrustlineDto): Trustline => ({
  id: String(dto.id ?? ''),
  investorId: String(dto.investorId ?? ''),
  cryptoWalletId: String(dto.cryptoWalletId ?? ''),
  propertyId: String(dto.propertyId ?? ''),
  tokenSymbol: dto.tokenSymbol ?? '',
  issuerWalletAddress: dto.issuerWalletAddress ?? '',
  trustlineStatus: toTrustlineStatus(dto.trustlineStatus),
  activatedAt: dto.activatedAt ?? '',
  failedAt: dto.failedAt ?? '',
  failureReason: dto.failureReason ?? '',
  createdAt: dto.createdAt ?? '',
  txHash: dto.txHash ?? '',
  // Null-preserved: it is what lets the status-check toast distinguish "the
  // issuer has not authorised" from "the read told us nothing".
  peerAuthorized: toPeerAuthorized(dto.peerAuthorized),
})

export const parseCryptoWalletAccount = (dto: CryptoWalletAccountDto): CryptoWalletAccount => ({
  id: String(dto.id ?? ''),
  investorId: String(dto.investorId ?? ''),
  walletAddress: dto.walletAddress ?? '',
  blockchainNetwork: dto.blockchainNetwork ?? '',
  validationMethod: dto.validationMethod ?? '',
  walletStatus: toWalletStatus(dto.walletStatus),
  isPrimary: Boolean(dto.isPrimary),
  validatedAt: dto.validatedAt ?? '',
  createdAt: dto.createdAt ?? '',
})
